import { useState, useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
} from 'chart.js';
import Config from '../../../Config';

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale
);

const PieChart = ({ leadSourceSummary }) => {
    const [activeTab, setActiveTab] = useState('total');

    // Source mapping from Config
    const source = [
        { value: Config.SOURCE.FACEBOOK, label: 'Facebook' },
        { value: Config.SOURCE.GOOGLE, label: 'Google' },
        { value: Config.SOURCE.INSTAGRAM, label: 'Instagram' },
        { value: Config.SOURCE.FLATMATE, label: 'Flatmate.in' },
        { value: Config.SOURCE.WALKIN, label: 'Walk In' },
        { value: Config.SOURCE.WEBSITE, label: 'Website' },
        { value: Config.SOURCE.MAGICBRICKS, label: 'MagicBricks' },
        { value: Config.SOURCE.ACRES, label: '99acres' },
        { value: Config.SOURCE.HOUSING, label: 'Housing' },
        { value: Config.SOURCE.NOBROKER, label: 'NoBroker' },
        { value: Config.SOURCE.OLX, label: 'Olx' },
        { value: Config.SOURCE.QUIKR, label: 'Quikr' },
        { value: Config.SOURCE.OTHERS, label: 'Others' },
    ];

    // Create a map for quick lookup
    const sourceMap = useMemo(() => {
        const map = {};
        source.forEach(item => {
            map[item.value] = item.label;
        });
        return map;
    }, []);

    // Color palette for different sources
    const colorPalette = [
        '#3B82F6', // Blue
        '#10B981', // Green
        '#F59E0B', // Amber
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#EC4899', // Pink
        '#14B8A6', // Teal
        '#F97316', // Orange
        '#6366F1', // Indigo
        '#84CC16', // Lime
        '#06B6D4', // Cyan
        '#D946EF', // Fuchsia
        '#F43F5E', // Rose
        '#0EA5E9', // Sky Blue
        '#8B5CF6', // Violet
        '#64748B', // Slate
    ];

    const getSourceLabel = (sourceId) => {
        return sourceMap[sourceId] || `Source ${sourceId}`;
    };

    // Prepare data based on active tab
    const chartData = useMemo(() => {
        if (!leadSourceSummary || leadSourceSummary.length === 0) {
            return {
                labels: ['No Data'],
                datasets: [{
                    data: [1],
                    backgroundColor: ['#E5E7EB'],
                    borderColor: ['#D1D5DB'],
                    borderWidth: 1,
                }]
            };
        }

        // Sort by selected metric descending for better visualization
        const sortedData = [...leadSourceSummary].sort((a, b) => {
            const getValue = (item) => {
                switch (activeTab) {
                    case 'total': return item.totalLeads;
                    case 'active': return item.activeLeads;
                    case 'pendingFollowUps': return item.pendingFollowUps;
                    case 'pendingVisits': return item.pendingVisits;
                    case 'converted': return item.convertedLeads;
                    case 'lost': return item.lostLeads;
                    default: return item.totalLeads;
                }
            };
            return getValue(b) - getValue(a);
        });

        const labels = sortedData.map(item => getSourceLabel(item.source));
        const data = sortedData.map(item => {
            switch (activeTab) {
                case 'total': return item.totalLeads;
                case 'active': return item.activeLeads;
                case 'pendingFollowUps': return item.pendingFollowUps;
                case 'pendingVisits': return item.pendingVisits;
                case 'converted': return item.convertedLeads;
                case 'lost': return item.lostLeads;
                default: return item.totalLeads;
            }
        });

        // Filter out zero values
        const filteredData = data.filter(value => value > 0);
        const filteredLabels = labels.filter((_, index) => data[index] > 0);

        // If all values are zero, show no data
        if (filteredData.length === 0) {
            return {
                labels: ['No Data'],
                datasets: [{
                    data: [1],
                    backgroundColor: ['#E5E7EB'],
                    borderColor: ['#D1D5DB'],
                    borderWidth: 1,
                }]
            };
        }

        // Assign colors to filtered data
        const colors = filteredData.map((_, index) =>
            colorPalette[index % colorPalette.length]
        );

        return {
            labels: filteredLabels,
            datasets: [
                {
                    data: filteredData,
                    backgroundColor: colors,
                    borderColor: colors.map(color => color),
                    borderWidth: 2,
                    hoverOffset: 8,
                },
            ],
        };
    }, [leadSourceSummary, activeTab, sourceMap]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            // legend: {
            //     position: 'right',
            //     labels: {
            //         padding: 20,
            //         usePointStyle: true,
            //         pointStyle: 'circle',
            //         font: {
            //             size: 12,
            //             weight: '500',
            //         },
            //         color: '#374151',
            //     },
            // },
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((context.parsed / total) * 100).toFixed(1);
                        return `${context.label}: ${context.parsed} leads (${percentage}%)`;
                    }
                }
            }
        },
        cutout: '60%',
    };

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        if (!leadSourceSummary || leadSourceSummary.length === 0) {
            return {
                totalLeads: 0,
                activeLeads: 0,
                pendingFollowUps: 0,
                pendingVisits: 0,
                convertedLeads: 0,
                lostLeads: 0,
                topSource: 'N/A',
                topSourceCount: 0
            };
        }

        const totalLeads = leadSourceSummary.reduce((sum, item) => sum + item.totalLeads, 0);
        const activeLeads = leadSourceSummary.reduce((sum, item) => sum + item.activeLeads, 0);
        const pendingFollowUps = leadSourceSummary.reduce((sum, item) => sum + item.pendingFollowUps, 0);
        const pendingVisits = leadSourceSummary.reduce((sum, item) => sum + item.pendingVisits, 0);
        const convertedLeads = leadSourceSummary.reduce((sum, item) => sum + item.convertedLeads, 0);
        const lostLeads = leadSourceSummary.reduce((sum, item) => sum + item.lostLeads, 0);

        // Find top source
        let topSource = '';
        let topSourceCount = 0;
        leadSourceSummary.forEach(item => {
            if (item.totalLeads > topSourceCount) {
                topSourceCount = item.totalLeads;
                topSource = getSourceLabel(item.source);
            }
        });

        return {
            totalLeads,
            activeLeads,
            pendingFollowUps,
            pendingVisits,
            convertedLeads,
            lostLeads,
            topSource,
            topSourceCount
        };
    }, [leadSourceSummary]);

    const tabs = [
        { id: 'total', label: 'Total Leads' },
        // { id: 'active', label: 'Active' },
        { id: 'pendingFollowUps', label: 'Follow-ups' },
        { id: 'pendingVisits', label: 'Visits' },
        { id: 'converted', label: 'Converted' },
        { id: 'lost', label: 'Lost' },
    ];

    return (
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-4">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        Lead Source Distribution
                    </h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                        {activeTab === 'total' ? 'Total leads by source' :
                            activeTab === 'active' ? 'Active leads by source' :
                                activeTab === 'pendingFollowUps' ? 'Pending follow-ups by source' :
                                    activeTab === 'pendingVisits' ? 'Pending visits by source' :
                                        activeTab === 'converted' ? 'Converted leads by source' :
                                            'Lost leads by source'}
                    </p>
                </div>
            </div>

            {/* Tab buttons */}
            <div className="flex flex-wrap gap-1.5 mb-4">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap
                            ${activeTab === tab.id
                                ? 'bg-primary-color text-white shadow-sm'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Chart */}
            <div className="h-72">
                {leadSourceSummary && leadSourceSummary.length > 0 ? (
                    <Doughnut data={chartData} options={options} />
                ) : (
                    <div className="h-full flex items-center justify-center text-gray-500">
                        No data available
                    </div>
                )}
            </div>

            {/* Summary Stats */}
            <div className="mt-4 grid grid-cols-3 gap-3">
                <StatCard
                    label="Total Leads"
                    value={summaryStats.totalLeads}
                    color="text-blue-600"
                />
                {/* <StatCard
                    label="Active"
                    value={summaryStats.activeLeads}
                    color="text-green-600"
                /> */}
                <StatCard
                    label="Follow-ups"
                    value={summaryStats.pendingFollowUps}
                    color="text-amber-600"
                />
                <StatCard
                    label="Pending Visits"
                    value={summaryStats.pendingVisits}
                    color="text-purple-600"
                />
                <StatCard
                    label="Converted"
                    value={summaryStats.convertedLeads}
                    color="text-emerald-600"
                />
                <StatCard
                    label="Lost"
                    value={summaryStats.lostLeads}
                    color="text-red-600"
                />
            </div>

            {/* Top Source */}
            {summaryStats.topSource && summaryStats.topSourceCount > 0 && (
                <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                    <p className="text-xs text-gray-600">
                         Top Source: <span className="font-bold text-gray-800">{summaryStats.topSource}</span>
                        {' '}with <span className="font-bold text-blue-600">{summaryStats.topSourceCount}</span> leads
                    </p>
                </div>
            )}
        </div>
    );
};

const StatCard = ({ label, value, color }) => {
    return (
        <div className="bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
            <p className="text-xs text-gray-500 font-medium uppercase truncate">{label}</p>
            <p className={`text-lg font-bold ${color}`}>
                {value}
            </p>
        </div>
    );
};

export default PieChart;